/**
 * Side-rail entry of collections (gated by `enableCollections`): opens /collect and shows a badge with
 * the number of new things (stickers to stick in + creature cards not opened yet). It also keeps the
 * local drawing reminders' text fresh with kind, situation-aware copy (core `reminderCopy`) whenever
 * local reminders are the active channel (native, reminders on, remote push not in charge).
 */
import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { reminderCopy } from '@sketchling/core';
import { useActiveProfile, useApp } from '../../state/store';
import { useLoc, useT } from '../../lib/i18n';
import { play } from '../../lib/sfx';
import { isNative, scheduleReminders } from '../../lib/platform';
import { Icon } from '../icons';
import { daysSinceLast, useCollectData, useToday } from '../../screens/collect/useCollect';

function useReminderCopySync() {
  const loc = useLoc();
  const profile = useActiveProfile();
  const lang = useApp((s) => s.settings.language);
  const reminders = useApp((s) => s.settings.reminders);
  const signedIn = useApp((s) => !!s.account.accessToken);
  const remote = useApp((s) => s.config.enableRemotePush !== false);
  const habit = useApp((s) => s.config.enableDailyHabit !== false);
  const today = useToday();
  const { rhythm, days, today: day } = useCollectData();
  const promptTitle = today ? loc(today.drawing.title) : '';
  const minutes = today?.minutes ?? 7;
  const since = daysSinceLast(days, day);
  useEffect(() => {
    if (!habit || !isNative || !reminders.enabled || !reminders.days.length || (signedIn && remote)) return;
    const copy = reminderCopy({ lang, name: profile?.name ?? '', day, goal: rhythm.goal, weekCount: rhythm.count, daysSinceLast: since, prompt: promptTitle ? { title: promptTitle, minutes } : null });
    void scheduleReminders(reminders.days, reminders.time, copy.title, copy.body).catch(() => false);
  }, [habit, reminders, signedIn, remote, lang, profile?.name, day, rhythm.goal, rhythm.count, since, promptTitle, minutes]);
}

export function CollectRailItem() {
  const t = useT();
  const loc = useLocation();
  const enabled = useApp((s) => s.config.enableCollections !== false);
  const ftue = useApp((s) => (s.activeProfileId ? s.progress[s.activeProfileId]?.ftue.stage === 'playground' : true));
  const { tray, fresh } = useCollectData();
  useReminderCopySync();
  if (!enabled || ftue) return null;
  const n = tray.length + fresh;
  return (
    <NavLink to="/collect" className="rail-item" aria-current={loc.pathname.startsWith('/collect') ? 'page' : undefined} data-testid="tab-collect" onClick={() => play('click')}>
      <Icon name="sticker" />
      <span>{t('col.rail')}</span>
      {n > 0 && (
        <span className="badge-dot" data-testid="collect-badge">
          {n > 9 ? '9+' : n}
        </span>
      )}
    </NavLink>
  );
}
